// Production-safe demo data for the public landing page. Built so the real
// app controls (UpcomingTasks, HourMeter, TaskCard, TaskLine,
// LogbookEntrySummary, StatusBadge) can be rendered with realistic data
// without touching the test-utils fixtures (which import JSON from /test).
//
// The dataset is anchored to "now" so Overdue / Soon / Not Due buckets stay
// honest as time passes. Re-evaluate by calling the loaders each render;
// every call returns a fresh deep copy so callers can safely mutate.

import * as TaskBuckets from '../fleet-shared/TaskBuckets.mjs';

import { groupTrips } from '../fleet-shared/TripGroups.mjs';

const clone = (value) => JSON.parse(JSON.stringify(value));

const DEMO_ITEM_ID = 'demo-item-enterprise';
const HOURS_COUNTER_ID = 'demo-counter-hours';
const ODOMETER_COUNTER_ID = 'demo-counter-odometer';

// Build a date offset from today by `days` (negative = past) at local
// midnight, then serialize to an ISO date string the way the app stores
// `dueDate` values.
function dateOffset(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// The canonical demo Item. Mirrors the AppSync shape (nested `items`
// arrays) so it can be fed straight into TaskBuckets.taskBuckets().
function buildDemoItem() {
  const hoursNow = 1875;
  const odometerNow = 48210;

  return {
    id: DEMO_ITEM_ID,
    name: "Enterprise",
    description: 'Grand Banks 42 Europa',
    itemType: 'boat',
    manufacturer: 'Grand Banks',
    serialNumber: '49',
    itemCounter: {
      items: [
        {
          id: HOURS_COUNTER_ID,
          name: 'Engine Hours',
          counter: hoursNow,
          itemId: DEMO_ITEM_ID,
        },
        {
          id: ODOMETER_COUNTER_ID,
          name: 'Nautical Miles',
          counter: odometerNow,
          itemId: DEMO_ITEM_ID,
        },
      ],
    },
    tasks: {
      items: [
        {
          id: 'demo-task-bottom-paint',
          name: 'Bottom paint',
          description: 'Haul out, sand, two coats of antifouling',
          notes: '',
          itemId: DEMO_ITEM_ID,
          dueCounter: null,
          dueDate: dateOffset(-12),
          counterInterval: null,
          daysInterval: 730,
          itemCounterId: null,
          assignedEmail: '',
          priority: 1,
        },
        {
          id: 'demo-task-oil-change',
          name: 'Oil change',
          description: 'Drain, replace filter, fill with 15W-40',
          notes: '',
          itemId: DEMO_ITEM_ID,
          dueCounter: 1900,
          dueDate: null,
          counterInterval: 100,
          daysInterval: null,
          itemCounterId: HOURS_COUNTER_ID,
          assignedEmail: '',
          priority: 1,
        },
        {
          id: 'demo-task-smoke-detectors',
          name: 'Replace smoke-detector batteries',
          description: '',
          notes: '',
          itemId: DEMO_ITEM_ID,
          dueCounter: null,
          dueDate: dateOffset(18),
          counterInterval: null,
          daysInterval: null,
          monthsInterval: 12,
          itemCounterId: null,
          assignedEmail: '',
          priority: 0,
        },
        {
          id: 'demo-task-tire-rotation',
          name: 'Replace fuel filters',
          description: '',
          notes: '',
          itemId: DEMO_ITEM_ID,
          dueCounter: 50000,
          dueDate: null,
          counterInterval: 5000,
          daysInterval: null,
          itemCounterId: ODOMETER_COUNTER_ID,
          assignedEmail: '',
          priority: 0,
        },
        {
          id: 'demo-task-coast-guard',
          name: 'Coast Guard inspection',
          description: 'Annual safety check',
          notes: '',
          itemId: DEMO_ITEM_ID,
          dueCounter: null,
          dueDate: dateOffset(120),
          counterInterval: null,
          daysInterval: 365,
          itemCounterId: null,
          assignedEmail: '',
          priority: 0,
        },
      ],
    },
    completedTasks: {
      items: [
        {
          id: 'demo-log-1',
          name: 'Oil change',
          itemId: DEMO_ITEM_ID,
          taskId: 'demo-task-oil-change',
          itemCounterId: HOURS_COUNTER_ID,
          counter: 1800,
          dueCounter: 1800,
          counterInterval: 100,
          date: dateOffset(-40),
          notes: '',
        },
        {
          id: 'demo-log-2',
          name: 'Zincs replaced',
          itemId: DEMO_ITEM_ID,
          taskId: null,
          itemCounterId: null,
          counter: 0,
          dueCounter: null,
          date: dateOffset(-65),
          notes: '',
        },
        {
          id: 'demo-log-3',
          name: 'Coast Guard inspection',
          itemId: DEMO_ITEM_ID,
          taskId: null,
          itemCounterId: null,
          counter: 0,
          dueCounter: null,
          date: dateOffset(-240),
          notes: 'Passed.',
        },
      ],
    },
    emails: { items: [] },
  };
}

export function demoItem() {
  return clone(buildDemoItem());
}

// The three dashboard buckets (Overdue / Soon / All) that UpcomingTasks
// renders. taskBuckets() also stamps each task with `bucketName`, which
// StatusBadge uses for its color.
export function demoBuckets() {
  return TaskBuckets.taskBuckets(demoItem());
}

// Flat list of tasks with `itemCounter` joined in (so TaskCard/TaskLine
// can render "1900 Engine Hours" and "every 100" interval text) and
// `bucketName` stamped on. Sorted Overdue → Soon → Not Due to mirror the
// production task list.
export function demoTaskRows() {
  const item = demoItem();
  const counterById = new Map(
    item.itemCounter.items.map((c) => [c.id, c])
  );

  const rows = item.tasks.items.map((task) => ({
    ...task,
    itemCounter: task.itemCounterId ? counterById.get(task.itemCounterId) : null,
  }));

  TaskBuckets.addBucketNameToTasks(rows);
  rows.sort(TaskBuckets.compareTasksOverdueFirst);

  return rows;
}

// A single representative task: the "Oil change" overdue
// against the Engine Hours counter. Useful for showing one TaskCard
// inline without an entire table.
export function demoFeaturedTask() {
  return demoTaskRows().find((t) => t.id === 'demo-task-oil-change')
    || demoTaskRows()[0];
}

// Logbook entries shaped for LogbookEntrySummary: { id, name, date }.
export function demoLogbookEntries() {
  return demoItem().completedTasks.items.map((entry) => ({
    id: entry.id,
    name: entry.name,
    date: entry.date,
    notes: entry.notes,
  }));
}

// Counter rows for HourMeter (display-only when no `save` prop is passed).
export function demoCounters() {
  return demoItem().itemCounter.items;
}

// ---- Trips ----------------------------------------------------------------

// Legs for the trip-planning demo, shaped as TaskPogoTrip rows and grouped
// through the real groupTrips() rather than hand-built as groups -- so the
// landing page's table is fed by the same code the app's trips list uses, and
// a change to how a group is summarised shows up here automatically.
//
// `days` on a leg is a stay: the boat arrives and sits for that many nights,
// which is what makes a trip's date range wider than its count of legs.
function demoTripLegs() {
  return [
    {
      id: 'demo-trip-leg-1',
      itemId: DEMO_ITEM_ID,
      tripGroupId: 'demo-trip-group-desolation',
      name: 'Desolation Sound',
      date: dateOffset(14),
      days: 1,
      from: 'Anacortes',
      to: 'Friday Harbor',
      distance: 22.4,
      notes: 'Slack at Thatcher Pass around 0930.',
    },
    {
      id: 'demo-trip-leg-2',
      itemId: DEMO_ITEM_ID,
      tripGroupId: 'demo-trip-group-desolation',
      name: 'Desolation Sound',
      date: dateOffset(15),
      days: 2,
      from: 'Friday Harbor',
      to: 'Nanaimo',
      distance: 41.8,
      notes: '',
    },
    {
      id: 'demo-trip-leg-3',
      itemId: DEMO_ITEM_ID,
      tripGroupId: 'demo-trip-group-desolation',
      name: 'Desolation Sound',
      date: dateOffset(17),
      days: 3,
      from: 'Nanaimo',
      to: 'Squirrel Cove',
      distance: 68.1,
      notes: 'Fuel and water at Lund.',
    },
    {
      id: 'demo-trip-leg-4',
      itemId: DEMO_ITEM_ID,
      tripGroupId: 'demo-trip-group-sanjuans',
      name: 'San Juans weekend',
      date: dateOffset(-21),
      days: 1,
      from: 'Anacortes',
      to: 'Sucia Island',
      distance: 18.6,
      notes: '',
    },
    {
      id: 'demo-trip-leg-5',
      itemId: DEMO_ITEM_ID,
      tripGroupId: 'demo-trip-group-sanjuans',
      name: 'San Juans weekend',
      date: dateOffset(-20),
      days: 1,
      from: 'Sucia Island',
      to: 'Anacortes',
      distance: 18.6,
      notes: 'Ebb against us the whole way back.',
    },
  ];
}

// Trip groups for TripGroupsTable, newest first the way the real list sorts.
export function demoTripGroups() {
  return groupTrips(demoTripLegs()).sort(
    (a, b) => new Date(b.startDate) - new Date(a.startDate)
  );
}

// Captain's Log entries: the passages already made, as opposed to the trips
// still being planned. Flat (already-split) shape, matching what the Captain's
// Log field registry stores -- a real entry keeps `from`/`to`/`hours` in the
// `fields` blob, but the landing page only reads them.
export function demoCaptainsEntries() {
  return [
    {
      id: 'demo-captains-1',
      tripName: 'San Juans weekend',
      date: dateOffset(-20),
      from: 'Sucia Island',
      to: 'Anacortes',
      hours: 3.5,
    },
    {
      id: 'demo-captains-2',
      tripName: 'San Juans weekend',
      date: dateOffset(-21),
      from: 'Anacortes',
      to: 'Sucia Island',
      hours: 3.2,
    },
    {
      id: 'demo-captains-3',
      tripName: 'Princess Louisa',
      date: dateOffset(-58),
      from: 'Egmont',
      to: 'Princess Louisa Inlet',
      hours: 4.1,
    },
  ];
}
